import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import {
  CONTENT_VIEW,
  LANGUAGES_SHORT,
  PROJECT_TYPES,
  PROJECT_TYPES_API,
  PROJECT_TYPES_ROUTES,
  RESONANCE_STRUCTURE,
  RESONANCE_THEMES,
  RESONANCE_VISIONS,
} from '../../constants';
import ProjectsFilters from './components/ProjectsFilters';
import ProjectsTable from './components/ProjectsTable';
import ProjectsCards from './components/ProjectsCards';
import CommonPagination from '../../components/common-pagination/CommonPagination';
import { changeTablePage, resetParams } from '../../redux/projects/projectsReducer';
import callApi from '../../service/callApi';
import { somethingWrongNotification } from '../../redux/notifier/notifierActions';
import { deactivateProject, deleteProject, getProjects } from '../../redux/projects/projectsActions';
import ConfirmationModal from '../../components/confirmation-modal/ConfirmationModal';
import DeleteLabel from './components/DeleteLabel';
import DeactivateProjectLabel from './components/DeactivateProjectLabel';
import ShareModal from './components/share-modal/ShareModal';
import CommonPage from '../../components/common-page/CommonPage';

import scss from './ProjectsList.module.scss';

const ProjectsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [contentView, setContentView] = useState(localStorage.getItem('PROJECTS_CONTENT_VIEW') ?? CONTENT_VIEW.CARDS);
  const [selectedProject, setSelectedProject] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const { tableSettings, totalElements } = useSelector((state) => state.projects);
  const serverUrl = useSelector((state) => state.securityNew.serverUrl);
  const { page = 0, size = 10 } = tableSettings || {};

  const handleChangePage = useCallback(
    (newPage) => {
      dispatch(changeTablePage(newPage));
    },
    [dispatch],
  );

  const handleChangeContentView = useCallback((newView) => {
    setContentView(newView);
    localStorage.setItem('PROJECTS_CONTENT_VIEW', newView);
  }, []);

  const redirectToResonance = useCallback(
    async ({
      id,
      theme = RESONANCE_THEMES.FACTIVE,
      vision = RESONANCE_VISIONS.DEFAULT,
      dataStructure = RESONANCE_STRUCTURE.RESPONDENTS,
      lang = LANGUAGES_SHORT.ENGLISH,
    }) => {
      const response = await callApi.get(`/api/ivis/token/generate`);

      if (response.status === 200) {
        const query = [
          `sourceId=${id}`,
          `token=${response.data.token}`,
          `theme=${theme.toLowerCase()}`,
          `vision=${vision.toLowerCase()}`,
          `dataStructure=${dataStructure.toLowerCase()}`,
          `lang=${lang.toLowerCase()}`,
        ];

        window.open(`${serverUrl}/chart/resonance/?${query.join('&')}`, '_blank').focus();
      } else {
        dispatch(somethingWrongNotification());
      }
    },
    [dispatch, serverUrl],
  );

  const handleOpenProject = useCallback(
    async (project) => {
      if (project.projectType === PROJECT_TYPES.RESONANCE) {
        const { id, theme, vision, dataStructure, lang } = project;

        if (dataStructure === RESONANCE_STRUCTURE.RESPONDENTS) {
          navigate(`/project/resonance/${id}`);
        }

        if (dataStructure === RESONANCE_STRUCTURE.MESSAGES) {
          redirectToResonance({ id, theme, vision, dataStructure, lang });
        }

        return;
      }

      const projectTypeRoute = PROJECT_TYPES_ROUTES[project.projectType];

      if (projectTypeRoute) {
        navigate(`/project/${projectTypeRoute}/${project.id}`);
      }
    },
    [redirectToResonance, navigate],
  );

  const handleEditProject = useCallback(
    ({ id, projectType }) => {
      const overridePath = projectTypeUrlOverrides[projectType];

      if (overridePath) {
        navigate(`edit/${overridePath}/${id}`);
        return;
      }

      navigate(`edit/${projectType.toLowerCase()}/${id}`);
    },
    [navigate],
  );

  const handleCloseModals = useCallback(() => {
    setIsDeleteModalOpen(false);
    setIsDeactivateModalOpen(false);
    setIsShareModalOpen(false);
  }, []);

  const handleOpenDeleteModal = useCallback((project) => {
    setSelectedProject(project);
    setIsDeleteModalOpen(true);
  }, []);

  const handleOpenShareModal = useCallback((project) => {
    setSelectedProject(project);
    setIsShareModalOpen(true);
  }, []);

  const handleOpenDeactivateModal = useCallback((project) => {
    setSelectedProject(project);
    setIsDeactivateModalOpen(true);
  }, []);

  const handleDeleteProject = useCallback(() => {
    dispatch(
      deleteProject({
        id: selectedProject.id,
        projectType: PROJECT_TYPES_API[selectedProject?.projectType] ?? selectedProject?.projectType?.toLowerCase(),
      }),
    );
  }, [dispatch, selectedProject]);

  const handleDeactivateProject = useCallback(() => {
    dispatch(deactivateProject({ id: selectedProject.id, active: selectedProject.active }));
  }, [dispatch, selectedProject]);

  const handleCollectData = useCallback(
    (id) => {
      navigate(`/project/sonar/collect-data/${id}`);
    },
    [navigate],
  );

  useEffect(() => {
    dispatch(getProjects());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableSettings]);

  useEffect(() => {
    return () => {
      dispatch(resetParams());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CommonPage>
      <ProjectsFilters onChangeContentView={handleChangeContentView} contentView={contentView} />

      <div className={scss.tableWrapper}>
        {contentView === CONTENT_VIEW.TABLE ? (
          <ProjectsTable
            onOpenProject={handleOpenProject}
            onShareProject={handleOpenShareModal}
            onEditProject={handleEditProject}
            onDeleteProject={handleOpenDeleteModal}
            onDeactivateProject={handleOpenDeactivateModal}
            onCollectData={handleCollectData}
          />
        ) : null}
        {contentView === CONTENT_VIEW.CARDS ? (
          <ProjectsCards
            onOpenProject={handleOpenProject}
            onShareProject={handleOpenShareModal}
            onEditProject={handleEditProject}
            onDeleteProject={handleOpenDeleteModal}
            onDeactivateProject={handleOpenDeactivateModal}
            onCollectData={handleCollectData}
          />
        ) : null}

        <div className={scss.paginationWrapper}>
          <CommonPagination
            activePage={page}
            itemsPerPage={size}
            totalItemsCount={totalElements}
            onChangePage={handleChangePage}
          />
        </div>
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModals}
        onCancel={handleCloseModals}
        onConfirm={handleDeleteProject}
        subLabel={<DeleteLabel name={selectedProject.projectName} />}
      />

      <ConfirmationModal
        isOpen={isDeactivateModalOpen}
        onClose={handleCloseModals}
        onCancel={handleCloseModals}
        onConfirm={handleDeactivateProject}
        subLabel={<DeactivateProjectLabel isActive={selectedProject.active} name={selectedProject.projectName} />}
      />

      <ShareModal isOpen={isShareModalOpen} onClose={handleCloseModals} project={selectedProject} />
    </CommonPage>
  );
};

export default memo(ProjectsList);

const projectTypeUrlOverrides = {
  [PROJECT_TYPES.GENIE_GO]: 'genie-go',
};
